import React, { useState, useEffect, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import Login from './components/Login';
import './App.css';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import PreSignInNavbar from './components/PreSignInNavbar';
import About from './pages/About';
import CustomVideoPlayer from './components/CustomVideoPlayer';
import VideoSettings from './pages/VideoSettings';
import MicButton from './components/MicButton';
import BottomMicButton from './components/BottomMicButton';
import AdminPanel from './pages/AdminPanel';
import SyllabusUpload from './pages/SyllabusUpload';
import Privacy from './pages/Privacy';

const VideoGenerator = () => {
  const [topic, setTopic] = useState('');
  const [narrator, setNarrator] = useState('');
  const [concept, setConcept] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [videoGenerated, setVideoGenerated] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  const handleGenerate = async () => {
    if (topic.trim()) {
      setIsGenerating(true);
      setError(null);
      setProgress(0);
      setProgressMessage('Starting video generation...');
      
      try {
        const videoSettings = JSON.parse(localStorage.getItem('videoSettings'));
        const prompt = `Create an educational cartoon about ${topic}` + 
                      (narrator ? ` narrated by ${narrator}` : '') + 
                      (concept ? ` explained in ${concept}` : '');
        
        console.log("Sending request with prompt:", prompt);

        const response = await fetch('/api/generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            prompt,
            settings: videoSettings 
          }),
        });

        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');
          
          for (const line of lines) {
            if (line.trim() === '') continue;
            
            try {
              const data = JSON.parse(line);
              if (data.progress) {
                setProgress(data.progress);
                setProgressMessage(data.message || '');
              } else if (data.video_path) {
                setVideoUrl(data.video_path);
                setQuestions(data.questions || []);
                setVideoGenerated(true);
              }
            } catch (e) {
              console.error('Error parsing chunk:', e);
            }
          }
        }
      } catch (err) {
        console.error('Error generating video:', err);
        setError('Failed to generate video. Please try again.');
      } finally {
        setIsGenerating(false);
      }
    }
  };

  return (
    <div className="video-generator">
      <div className="form-group">
        <input
          type="text"
          placeholder="Enter any topic"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          className="topic-input"
        />
      </div>
      
      <div className="form-group">
        <input
          type="text"
          placeholder="Who should narrate? (e.g. Snoop Dogg, Donald Trump, LeBron James)"
          value={narrator}
          onChange={(e) => setNarrator(e.target.value)}
          className="topic-input"
        />
      </div>
      
      <div className="form-group">
        <input
          type="text"
          placeholder="How should it be explained? (e.g. using Music Festival terms, NBA terms, World Cup terms)"
          value={concept}
          onChange={(e) => setConcept(e.target.value)}
          className="topic-input"
        />
      </div>
      
      <button 
        onClick={handleGenerate}
        disabled={!topic.trim() || isGenerating}
        className="generate-btn"
      >
        {isGenerating ? 'Generating...' : 'Go!'}
      </button>
      
      {error && <div className="error-message">{error}</div>}
      
      {isGenerating && (
        <div className="loading-placeholder">
          <p>Creating your educational cartoon...</p>
          <div className="loading-bar-container">
            <div className="loading-bar" style={{ width: `${progress}%` }}></div>
          </div>
          <p className="loading-text">{progressMessage}</p>
          <p className="loading-suggestion">This may take a few minutes. In the meantime, ask any questions below!</p>
        </div>
      )}
      
      {videoGenerated && (
        <div className="video-container">
          <CustomVideoPlayer 
            videoUrl={videoUrl}
            questions={questions}
            narrations={[]}
          />
        </div>
      )}
    </div>
  );
};

const SearchBar = () => {
  const [question, setQuestion] = useState('');
  const [response, setResponse] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const handleAskAI = async () => {
    if (question.trim()) {
      setIsSearching(true);
      
      try {
        // Call the backend search API
        const response = await fetch('/api/search', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ query: question }),
        });
        
        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }
        
        const data = await response.json();
        setResponse(data.response);
      } catch (err) {
        console.error('Error getting AI response:', err);
        setResponse('Sorry, I had trouble processing your question. Please try again.');
      } finally {
        setIsSearching(false);
      }
    }
  };

  return (
    <div className="search-section">
      <div className="search-input-container">
        <input
          type="text"
          placeholder="Ask away..."
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          className="search-input"
          onKeyPress={(e) => e.key === 'Enter' && handleAskAI()}
        />
        <button 
          onClick={handleAskAI}
          disabled={!question.trim() || isSearching}
          className="ask-btn"
        >
          {isSearching ? 'Thinking...' : 'Ask AI'}
        </button>
      </div>
      
      {response && (
        <div className="ai-response">
          <h4>AI Response:</h4>
          <div className="response-content" dangerouslySetInnerHTML={{ __html: response.replace(/\n/g, '<br>') }}></div>
        </div>
      )}
    </div>
  );
};

const MainApp = () => {
  return (
    <div className="app">
      <Navbar />
      <Routes>
        <Route path="/" element={
          <div className="main-container">
            <div style={{
              width: '100%',
              maxWidth: '800px',
              padding: '1rem'
            }}>
              <h2 style={{
                textAlign: 'center',
                marginBottom: '1.5rem',
                color: '#333',
                fontSize: '1.2rem',
                fontWeight: '500'
              }}>
                Watch Our Demo
              </h2>
              <div style={{
                position: 'relative',
                paddingBottom: '56.25%',
                height: 0,
                overflow: 'hidden',
                borderRadius: '8px',
              }}>
                <iframe
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '8px',
                  }}
                  src="https://www.youtube.com/embed/SjJJYhtFauo"
                  title="Visualize Demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        } />
        <Route path="/about" element={<About />} />
        <Route path="/video-settings" element={<VideoSettings />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/syllabus-upload" element={<SyllabusUpload />} />
        <Route path="/privacy" element={<Privacy />} />
      </Routes>
    </div>
  );
};

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = (user) => {
    setUser(user);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <Router>
      <div>
        {user ? (
          <MainApp />
        ) : (
          <>
            <PreSignInNavbar />
            <Routes>
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/" element={
                <div style={{
                  minHeight: '100vh',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '2rem',
                  backgroundColor: '#EEF2F6'
                }}>
                  <div style={{
                    width: '100%',
                    maxWidth: '1200px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '3rem',
                    alignItems: 'center',
                    paddingTop: '2rem'
                  }}>
                    <Login onLogin={handleLogin} />
                    <div style={{
                      width: '100%',
                      maxWidth: '800px',
                      padding: '1rem'
                    }}>
                      <h2 style={{
                        textAlign: 'center',
                        marginBottom: '1.5rem',
                        color: '#333',
                        fontSize: '1.2rem',
                        fontWeight: '500'
                      }}>
                        Watch Our Demo
                      </h2>
                      <div style={{
                        position: 'relative',
                        paddingBottom: '56.25%',
                        height: 0,
                        overflow: 'hidden',
                        borderRadius: '8px',
                      }}>
                        <iframe
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            borderRadius: '8px',
                          }}
                          src="https://www.youtube.com/embed/SjJJYhtFauo"
                          title="Visualize Demo"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  </div>
                </div>
              } />
            </Routes>
          </>
        )}
      </div>
    </Router>
  );
};

export default App;